<template>
  <div class="article-page-author-and-date">
    <a
      v-if="author"
      class="article-page-author-and-date__link"
      :href="authorLink"
    >
      <div class="article-page-author-and-date-author">
        <img
          class="article-page-author-and-date-author__avatar"
          :src="authorAvatarUrl"
          :alt="author.name"
        />
        <div class="article-page-author-and-date-author-info">
          <div class="article-page-author-and-date-author-info__name">{{ author.name }}</div>
          <div
            v-if="author.short_description"
            class="article-page-author-and-date-author-info__description"
          >
            {{ author.short_description }}
          </div>
        </div>
      </div>
    </a>

    <div class="article-page-author-and-date__datetime">
      <span>
        <span v-if="updatedAfterPublishing">Обновлено</span>&nbsp;<span>{{ datetime }}</span>
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import { calendarFormatMonths, getNumberOfDaysDiff } from '@/shared/utils/time';
  import type { ArticleAuthor } from '@/shared/api/experience';

  const defaultAvatarUrl = new URL('@/app/assets/default-avatar.png', import.meta.url).href;

  interface Props {
    author?: ArticleAuthor | null;
    publishedAt?: Date | string;
    updatedAt?: Date | string;
  }

  const props = defineProps<Props>();

  const authorLink = computed(() => `/journal/author/${props.author?.id}`);
  const authorAvatarUrl = computed(() => props.author?.avatar_url ?? defaultAvatarUrl);

  const updatedAfterPublishing = computed(() => {
    const published = props.publishedAt;
    const updated = props.updatedAt;
    if (published == null || updated == null) {
      return false;
    }
    return Boolean(getNumberOfDaysDiff(published, updated));
  });

  const datetime = computed(() => {
    const published = props.publishedAt;
    const updated = props.updatedAt;
    const value = updated ?? published;
    return value != null ? calendarFormatMonths(value) : '';
  });
</script>

<style lang="scss" scoped>
  .article-page-author-and-date {
    display: flex;
    flex-wrap: wrap;
    gap: var(--alias-space-16);
    justify-content: space-between;
    margin-top: var(--alias-space-32);

    @include tablet {
      margin-top: var(--alias-space-24);
    }

    @include mobile {
      flex-direction: column;
    }
  }

  .article-page-author-and-date__link {
    color: var(--alias-color-text-default);
    text-decoration: none;
  }

  .article-page-author-and-date-author {
    display: flex;
    gap: var(--alias-space-12);
  }

  .article-page-author-and-date-author__avatar {
    flex-grow: 0;
    flex-shrink: 0;
    object-fit: cover;
    width: 48px;
    height: 48px;
    border-radius: var(--alias-radius-rounded);
    background-color: var(--alias-color-background-default);
  }

  .article-page-author-and-date-author-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .article-page-author-and-date-author-info__name {
    @include base-500-400-medium;
  }

  .article-page-author-and-date-author-info__description {
    color: var(--alias-color-text-subtle);

    @include base-400-400-regular;
  }

  .article-page-author-and-date__datetime {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    color: var(--alias-color-text-subtle);

    @include base-400-300-regular;
  }
</style>
